/**
 * Shared section-marker contract for multi-section content-block descriptions.
 *
 * Two layout styles render several independent text columns (3 and 2
 * respectively — see `sectionCountForStyle`) but persist to a SINGLE
 * `description` scalar. To let each column own its own slice without a schema
 * change, the sections are stored in one string separated by an in-string
 * delimiter. Splitting/joining lives here so the admin editor and the live
 * portal stay byte-for-byte in sync.
 *
 * The delimiter is NEVER inserted into editor content or shown as rendered text
 * — each editor box loads only its own (delimiter-free) section slice, and the
 * delimiter is added back only by the join/save step.
 */

/** Literal in-string delimiter separating sections. */
export const SECTION_MARKER = ':::section-break:::'

/**
 * Split a stored description into its section strings. Surrounding newlines are
 * consumed. A legacy string with no delimiter yields `[whole]`, so section 0 is
 * the whole value and section 1 is `''`.
 */
export function splitSections(raw?: string): string[] {
  return (raw ?? '').split(/\n*:::section-break:::\n*/)
}

/** Join section strings back into a single stored description. */
export function joinSections(parts: string[]): string {
  return parts.join('\n\n:::section-break:::\n\n')
}

/**
 * How many independent text sections a style renders. The multi-column text
 * styles (`2_columns_title_top_center`, `2_columns_1_3`, `3_columns_title_left`)
 * render N text columns and no title, so their section count IS their column
 * count; every other style is single-section.
 *
 * Despite their names, none of these styles render a title — the count is
 * purely the number of text columns (`3_columns_title_left` = 3 text columns,
 * NOT a title column plus 2 text columns, which is what it meant before).
 * `2_columns_1_3` is the same 2-section shape as `2_columns_title_top_center`;
 * only the rendered column WIDTHS differ (1/3 + 2/3 vs. equal), and that only
 * matters in the portal.
 *
 * MUST match the column count the portal renderer maps across — see
 * `vilmers-b2b-portal/src/modules/home/components/content-block/index.tsx`
 * (`MultiColumnText`, via `OnlyText`). If these disagree, the admin editor and
 * the live site render a different number of columns.
 */
export function sectionCountForStyle(style: string): number {
  if (style === '2_columns_title_top_center') return 2
  if (style === '2_columns_1_3') return 2
  if (style === '3_columns_title_left') return 3
  return 1
}

/**
 * Return a new stored description with `text` set at `index`. For single-section
 * styles (`sectionCount <= 1`) the text is returned as-is with NO delimiter, so
 * existing non-multi-section blocks are byte-for-byte unaffected. For
 * multi-section styles the sections are split, padded, updated, then re-joined.
 */
export function setSection(
  raw: string | undefined,
  index: number,
  text: string,
  sectionCount: number
): string {
  const parts = splitSections(raw)
  while (parts.length < sectionCount) parts.push('')
  parts[index] = text
  if (sectionCount <= 1) return text
  return joinSections(parts)
}
